import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import useBodyClassName from 'hooks/useBodyClassName';
import dialNumber from 'utils/dialNumber';
import { Text, H3, Paragraph } from 'components/core/Typography';
import { Button } from 'components/core/Button';
import Phone from 'components/svg/Phone';
import Filter from 'components/svg/Filter';
import Chevron from 'components/svg/Chevron';
import { getLocalCityPhoneNumber as getLocalCityPhoneNumberAction } from 'store/modules/phone';

import './SearchTray.scss';

export function SearchTray({
	onShowSearchFilterModal,
	searchLocation,
	getLocalCityPhoneNumber: getLocalCityPhoneNumberProp,
	stickyTrayPhoneNumber,
	delay = 500,
	invisible,
	withinCallCenterHours,
}) {
	const dispatch = useDispatch();
	const getLocalCityPhoneNumber =
		getLocalCityPhoneNumberProp ??
		((...args) => dispatch(getLocalCityPhoneNumberAction(...args)));

	useBodyClassName('with-sticky-tray-padding');

	const [isExpanded, setIsExpanded] = React.useState(true);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isVisible, setIsVisible] = React.useState(false);

	const timeoutRef = React.useRef(null);
	const { formattedNumber, number } = stickyTrayPhoneNumber || {};

	// Start delayed render timer on component mount
	React.useEffect(() => {
		clearTimeout(timeoutRef.current);
		timeoutRef.current = setTimeout(() => {
			setIsVisible(true);
		}, delay);
		return () => clearTimeout(timeoutRef.current);
	}, [delay]);

	// Dial the allocated phone number once we've received it
	React.useEffect(() => {
		if (number) {
			dialNumber(number);
			setIsLoading(false);
		}
	}, [number]);

	// Allocate a phone number if one has not been already
	const buttonHandler = () => {
		if (number) {
			// If a desktop user wants to dial, they can this way
			dialNumber(number);
		} else {
			setIsLoading(true);
			getLocalCityPhoneNumber(searchLocation.city, searchLocation.state); // Should only happen once
		}
	};
	const titleText = "Can't Decide?";
	const subtitleText = 'Call us to help you find something faster.';

	const loadingText = 'Loading...';
	const mobileCallText = isLoading
		? loadingText
		: formattedNumber || 'Call Now';

	const shouldShowStickyTray = withinCallCenterHours;

	return (
		<div
			className={classnames('search-tray', {
				hidden: !isVisible || !shouldShowStickyTray,
				visible: isVisible && shouldShowStickyTray,
				invisible,
			})}
			aria-hidden={!isVisible || !shouldShowStickyTray}
		>
			<div className="content">
				<Button
					className="tab"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<Chevron direction={isExpanded ? 'down' : 'up'} />
				</Button>

				<div
					className={classnames('slider', {
						open: isExpanded,
						closed: !isExpanded,
					})}
					aria-expanded={isExpanded}
				>
					<div className="slider-inner">
						<H3
							color="white"
							className="title"
						>
							{titleText}
						</H3>
						<Paragraph
							color="lightGray"
							className="text"
						>
							{subtitleText}
						</Paragraph>
					</div>
				</div>

				<div className={classnames('buttons-container')}>
					<Button
						primary
						fullWidth
						onClick={buttonHandler}
						className={classnames('call-now-btn')}
					>
						{!isLoading && <Phone />}
						{mobileCallText}
					</Button>
					<Text color="white">or</Text>
					<Button
						secondary
						onClick={onShowSearchFilterModal}
						className={classnames('filter-btn')}
					>
						<Filter />
						Filter
					</Button>
				</div>
			</div>
		</div>
	);
}

SearchTray.propTypes = {
	onShowSearchFilterModal: PropTypes.func.isRequired,
	getLocalCityPhoneNumber: PropTypes.func,
	stickyTrayPhoneNumber: PropTypes.object,
	searchLocation: PropTypes.object,
	delay: PropTypes.number,
	invisible: PropTypes.bool,
	withinCallCenterHours: PropTypes.bool,
};

export default SearchTray;
